/*
 * Copyright (c) 2025, Glasswing AI, Inc.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 * Glasswing AI, Inc. ("Confidential Information").
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Glasswing AI, Inc.
 */
import { useParams, useNavigate } from 'react-router';
import { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Form, Button, ButtonToolbar } from 'react-bootstrap';
import { JsonEditor } from 'json-edit-react';
import { getAiConnector, createAiConnector, setAiConnector, AiConnector } from '../data';
import { StatusContext, StatusContextType } from './StatusContext';
import { useTheme } from 'styled-components';

type ConnectorVendor = 'perplexity' | 'perplexity_agent';

const DEFAULT_ENDPOINT = 'https://api.perplexity.ai';

const DEFAULT_DESCRIPTIONS: Record<ConnectorVendor, string> = {
    perplexity: 'Perplexity Sonar Pro',
    perplexity_agent: 'Perplexity Deep Research',
};

function buildDefaultConnection(vendor: ConnectorVendor): Record<string, any> {
    if (vendor === 'perplexity_agent') {
        return {
            vendor,
            api_endpoint: DEFAULT_ENDPOINT,
            vendor_options: {
                preset: 'deep-research',
                max_steps: 10,
                max_output_tokens: 10000,
                reasoning: {
                    effort: 'high',
                },
                tools: [
                    {
                        type: 'web_search',
                        max_tokens: 10000,
                        max_tokens_per_page: 4000,
                    },
                    {
                        type: 'fetch_url',
                        max_urls: 10,
                    },
                ],
            },
        };
    }

    return {
        vendor,
        api_endpoint: DEFAULT_ENDPOINT,
        vendor_options: {
            model: 'sonar-pro',
            max_tokens: 10000,
            web_search_options: {
                search_context_size: 'high',
            },
        },
    };
}

function buildDefaultConnector(vendor: ConnectorVendor = 'perplexity'): AiConnector {
    return {
        id: '',
        description: DEFAULT_DESCRIPTIONS[vendor],
        system_prompt: 'You are a careful research assistant. Cite sources and be explicit about uncertainty.',
        connection: buildDefaultConnection(vendor),
    };
}

function normalizeConnector(aiConnector: AiConnector): AiConnector {
    const vendor = aiConnector?.connection?.vendor === 'perplexity_agent' ? 'perplexity_agent' : 'perplexity';
    const normalized = buildDefaultConnector(vendor);

    return {
        ...normalized,
        ...aiConnector,
        description: aiConnector?.description || normalized.description,
        system_prompt: aiConnector?.system_prompt || normalized.system_prompt,
        connection: {
            ...normalized.connection,
            ...(aiConnector?.connection || {}),
            vendor,
            vendor_options: {
                ...normalized.connection.vendor_options,
                ...(aiConnector?.connection?.vendor_options || {}),
            },
        },
    };
}

function getTool(vendorOptions: Record<string, any>, toolType: string) {
    return Array.isArray(vendorOptions.tools)
        ? vendorOptions.tools.find((tool: any) => tool?.type === toolType)
        : undefined;
}

function buildDefaultTool(toolType: string) {
    if (toolType === 'fetch_url') {
        return { type: 'fetch_url', max_urls: 10 };
    }
    return { type: 'web_search', max_tokens: 10000, max_tokens_per_page: 4000 };
}

function parseOptionalNumber(value: string) {
    return value === '' ? undefined : Number(value);
}

function AiConnectorEditor() {
    const [aiConnectorData, setAiConnectorData] = useState<{ loading: boolean, data: AiConnector | null }>({ loading: true, data: null });
    const [showJsonEditor, setShowJsonEditor] = useState(true);
    const [descriptionDup, setDescriptionDup] = useState(false);
    const params = useParams<{ contextId?: string; connectorId?: string }>();
    const connectorId = params.connectorId || params.contextId;
    const statusContext = useContext<StatusContextType>(StatusContext);
    const theme: any = useTheme();
    const navigate = useNavigate();

    useEffect(() => {
        async function fetchData() {
            try {
                if (connectorId === 'new') {
                    setAiConnectorData({ loading: false, data: buildDefaultConnector() });
                } else {
                    const nextConnector = normalizeConnector(await getAiConnector(connectorId || ''));
                    setAiConnectorData({ loading: false, data: nextConnector });
                }
            } catch (error: any) {
                console.error('Error fetching data:', error);
                setAiConnectorData({ loading: false, data: null });
                statusContext.postError(`Failed to load data: ${error.message}`);
            }
        }
        fetchData();
    }, [connectorId, statusContext]);

    const handleChange = (field: keyof AiConnector, value: any) => {
        if (aiConnectorData.data) {
            setAiConnectorData({
                loading: false,
                data: {
                    ...aiConnectorData.data,
                    [field]: value
                }
            });
        }
    };

    const updateConnection = (updates: Record<string, any>) => {
        if (!aiConnectorData.data) {
            return;
        }
        setAiConnectorData({
            loading: false,
            data: normalizeConnector({
                ...aiConnectorData.data,
                connection: {
                    ...aiConnectorData.data.connection,
                    ...updates,
                },
            }),
        });
    };

    const updateVendorOptions = (updates: Record<string, any>) => {
        if (!aiConnectorData.data) {
            return;
        }
        updateConnection({
            vendor_options: {
                ...(aiConnectorData.data.connection.vendor_options || {}),
                ...updates,
            },
        });
    };

    const updateAgentTool = (toolType: string, updates: Record<string, any>) => {
        if (!aiConnectorData.data) {
            return;
        }
        const vendorOptions = aiConnectorData.data.connection.vendor_options || {};
        const tools = Array.isArray(vendorOptions.tools) ? [...vendorOptions.tools] : [];
        const toolIndex = tools.findIndex((tool: any) => tool?.type === toolType);
        const baseTool = toolIndex === -1 ? buildDefaultTool(toolType) : tools[toolIndex];
        const nextTool = {
            ...baseTool,
            ...updates,
        };

        if (toolIndex === -1) {
            tools.push(nextTool);
        } else {
            tools[toolIndex] = nextTool;
        }

        updateVendorOptions({ tools });
    };

    const toggleAgentTool = (toolType: string, enabled: boolean) => {
        if (!aiConnectorData.data) {
            return;
        }
        const vendorOptions = aiConnectorData.data.connection.vendor_options || {};
        const tools = Array.isArray(vendorOptions.tools) ? [...vendorOptions.tools] : [];
        const toolIndex = tools.findIndex((tool: any) => tool?.type === toolType);

        if (enabled && toolIndex === -1) {
            tools.push(buildDefaultTool(toolType));
        }
        if (!enabled && toolIndex !== -1) {
            tools.splice(toolIndex, 1);
        }

        updateVendorOptions({ tools });
    };

    const handleConnectionUpdate = ({ newData }: { newData: any }) => {
        if (aiConnectorData.data) {
            setAiConnectorData({
                loading: false,
                data: normalizeConnector({
                    ...aiConnectorData.data,
                    connection: newData,
                }),
            });
        }
    };

    const handleVendorChange = (vendor: ConnectorVendor) => {
        if (!aiConnectorData.data) {
            return;
        }
        const currentVendor = aiConnectorData.data.connection.vendor === 'perplexity_agent' ? 'perplexity_agent' : 'perplexity';
        const replaceDescription = !aiConnectorData.data.description
            || aiConnectorData.data.description === DEFAULT_DESCRIPTIONS[currentVendor]
            || aiConnectorData.data.description === 'New AI Context'
            || aiConnectorData.data.description === 'New AI Connector';

        setAiConnectorData({
            loading: false,
            data: normalizeConnector({
                ...aiConnectorData.data,
                description: replaceDescription ? DEFAULT_DESCRIPTIONS[vendor] : aiConnectorData.data.description,
                connection: buildDefaultConnection(vendor),
            }),
        });
    };

    const handleSave = async () => {
        if (!aiConnectorData.data) return;
        
        try {
            const nextConnector = normalizeConnector(aiConnectorData.data);
            if (connectorId === 'new') {
                const newId = await createAiConnector(nextConnector);
                if (newId === "-1") {
                    setDescriptionDup(true);
                    return;
                }
            } else {
                await setAiConnector(nextConnector);
            }
            navigate('/ai/connectors');
        } catch (error: any) {
            statusContext.postError(`Failed to save AI Connector: ${error.message}`);
        }
    };

    if (aiConnectorData.loading) {
        return (
            <Container fluid>
                <Row className="mt-4">
                    <Col>
                        <h2>Loading AI Connector data...</h2>
                    </Col>
                </Row>
            </Container>
        );
    }

    if (!aiConnectorData.data) {
        return (
            <Container fluid>
                <Row className="mt-4">
                    <Col>
                        <h2>Error: AI Connector not found</h2>
                    </Col>
                </Row>
            </Container>
        );
    }

    const { data } = aiConnectorData;
    const vendor = data.connection.vendor === 'perplexity_agent' ? 'perplexity_agent' : 'perplexity';
    const vendorOptions = data.connection.vendor_options || {};
    const webSearchTool = getTool(vendorOptions, 'web_search');
    const fetchUrlTool = getTool(vendorOptions, 'fetch_url');

    return (
        <div style={{ backgroundColor: theme.colors.bg, color: theme.colors.ctext }} className="container-fluid">
            <Container className="ms-4" fluid>
                <Row className="mt-2">
                    <Col className="col-6">
                        <h2>AI Connector Editor</h2>
                    </Col>
                    <Col className="col-6">
                        <ButtonToolbar className="float-end me-5">
                            <Button 
                                variant="secondary" 
                                onClick={() => navigate('/ai/connectors')} 
                                className="me-2"
                            >
                                Cancel
                            </Button>
                            <Button 
                                variant="success" 
                                onClick={handleSave}
                            >
                                Save
                            </Button>
                        </ButtonToolbar>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>ID</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.id || 'New'}
                                disabled
                                readOnly
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Description {descriptionDup ?
                                <span style={{ color: 'red' }}> - Duplicate, choose a unique description</span> : null}
                            </Form.Label>
                            <Form.Control
                                type="text"
                                value={data.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                placeholder="Enter connector description"
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>
                        <Form.Group className="mb-3">
                            <Form.Label>System Prompt</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                value={data.system_prompt}
                                onChange={(e) => handleChange('system_prompt', e.target.value)}
                                placeholder="Enter system prompt"
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Connector Type</Form.Label>
                            <Form.Select
                                value={vendor}
                                onChange={(e) => handleVendorChange(e.target.value as ConnectorVendor)}
                            >
                                <option value="perplexity">Perplexity Chat</option>
                                <option value="perplexity_agent">Perplexity Research / Deep Research</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={8}>
                        <Form.Group className="mb-3">
                            <Form.Label>API Endpoint</Form.Label>
                            <Form.Control
                                type="text"
                                value={data.connection.api_endpoint || ''}
                                onChange={(e) => updateConnection({ api_endpoint: e.target.value })}
                                placeholder={DEFAULT_ENDPOINT}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                {vendor === 'perplexity' && (
                    <>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Model</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={vendorOptions.model || ''}
                                        onChange={(e) => updateVendorOptions({ model: e.target.value })}
                                        placeholder="sonar-pro"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Max Output Tokens</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={vendorOptions.max_tokens ?? ''}
                                        onChange={(e) => updateVendorOptions({ max_tokens: parseOptionalNumber(e.target.value) })}
                                        placeholder="10000"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Search Context</Form.Label>
                                    <Form.Select
                                        value={vendorOptions.web_search_options?.search_context_size || 'high'}
                                        onChange={(e) => updateVendorOptions({
                                            web_search_options: {
                                                ...(vendorOptions.web_search_options || {}),
                                                search_context_size: e.target.value,
                                            },
                                        })}
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )}
                {vendor === 'perplexity_agent' && (
                    <>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Profile / Preset</Form.Label>
                                    <Form.Select
                                        value={vendorOptions.preset || 'deep-research'}
                                        onChange={(e) => updateVendorOptions({ preset: e.target.value })}
                                    >
                                        <option value="deep-research">Deep Research</option>
                                        <option value="sonar-pro">Sonar Pro</option>
                                        <option value="sonar-reasoning">Sonar Reasoning</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Model Override</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={vendorOptions.model || ''}
                                        onChange={(e) => updateVendorOptions({ model: e.target.value || undefined })}
                                        placeholder="provider/model"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Reasoning Effort</Form.Label>
                                    <Form.Select
                                        value={vendorOptions.reasoning?.effort || 'high'}
                                        onChange={(e) => updateVendorOptions({
                                            reasoning: {
                                                ...(vendorOptions.reasoning || {}),
                                                effort: e.target.value,
                                            },
                                        })}
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Max Steps</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={vendorOptions.max_steps ?? ''}
                                        onChange={(e) => updateVendorOptions({ max_steps: parseOptionalNumber(e.target.value) })}
                                        placeholder="10"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Max Output Tokens</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={vendorOptions.max_output_tokens ?? ''}
                                        onChange={(e) => updateVendorOptions({ max_output_tokens: parseOptionalNumber(e.target.value) })}
                                        placeholder="10000"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Language Preference</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={vendorOptions.language_preference || ''}
                                        onChange={(e) => updateVendorOptions({ language_preference: e.target.value || undefined })}
                                        placeholder="en"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Check
                                    className="mb-3"
                                    type="switch"
                                    label="Enable web search tool"
                                    checked={Boolean(webSearchTool)}
                                    onChange={(e) => toggleAgentTool('web_search', e.target.checked)}
                                />
                            </Col>
                            <Col md={6}>
                                <Form.Check
                                    className="mb-3"
                                    type="switch"
                                    label="Enable fetch URL tool"
                                    checked={Boolean(fetchUrlTool)}
                                    onChange={(e) => toggleAgentTool('fetch_url', e.target.checked)}
                                />
                            </Col>
                        </Row>
                        {webSearchTool && (
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Web Search Max Tokens</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={webSearchTool.max_tokens ?? ''}
                                            onChange={(e) => updateAgentTool('web_search', { max_tokens: parseOptionalNumber(e.target.value) })}
                                            placeholder="10000"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Per-Page Token Budget</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={webSearchTool.max_tokens_per_page ?? ''}
                                            onChange={(e) => updateAgentTool('web_search', { max_tokens_per_page: parseOptionalNumber(e.target.value) })}
                                            placeholder="4000"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                        {fetchUrlTool && (
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Fetch URL Limit</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={fetchUrlTool.max_urls ?? ''}
                                            onChange={(e) => updateAgentTool('fetch_url', { max_urls: parseOptionalNumber(e.target.value) })}
                                            placeholder="10"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}
                    </>
                )}
                <Row>
                    <Col md={12}>
                        <Form.Group className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="mb-0">Advanced Vendor Options</Form.Label>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => setShowJsonEditor((prev) => !prev)}
                                >
                                    {showJsonEditor ? 'Hide JSON' : 'Show JSON'}
                                </Button>
                            </div>
                            <Form.Text className="d-block mb-2" style={{ color: theme.colors.textMuted }}>
                                Use the form fields above for the supported settings. The JSON editor is available for vendor-specific options that are not surfaced yet.
                            </Form.Text>
                            {showJsonEditor && (
                                <div style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '10px', backgroundColor: '#f8f9fa' }}>
                                    <JsonEditor
                                        maxWidth="100%"
                                        data={data.connection}
                                        onUpdate={handleConnectionUpdate}
                                        collapse
                                    />
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>
            </Container>
        </div>
    );
}

export default AiConnectorEditor;
